
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MarginAnalysisState, MarginAnalysisActions } from './useMarginAnalysis';

interface FilterHeaderProps {
  state: MarginAnalysisState;
  actions: MarginAnalysisActions;
}

const FilterHeader: React.FC<FilterHeaderProps> = ({
  state,
  actions
}) => {
  const {
    filterType,
    selectedRegion,
    selectedCountry,
    selectedAccount,
    selectedProduct,
    selectedIndustry,
    regions,
    countries,
    accounts,
    products,
    industries
  } = state;

  const {
    handleFilterTypeChange,
    handleRegionChange,
    handleCountryChange,
    handleAccountNameChange,
    handleProductLineChange,
    handleIndustryChange
  } = actions;

  return (
    <Card className="bg-gradient-to-r from-[#1e293b] to-[#1a1f2c] border-none shadow-lg">
      <CardContent className="p-4 py-[4px] my-[10px] bg-secondary border-none shadow-none">
        <div className="flex justify-between items-center mb-0 bg-inherit">
          <h3 className="text-white text-lg font-semibold">Gross Profit Margin Analysis</h3>
          
          <div className="flex items-center gap-2 bg-inherit">
            <div className="flex items-center space-x-3">
              <span className="whitespace-nowrap font-medium text-primary-light">Filter by:</span>
            </div>
            
            <Select value={selectedRegion} onValueChange={handleRegionChange}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black border border-[#6366F1]/30">
                <SelectItem value="all">All</SelectItem>
                {regions.map(region => <SelectItem key={region} value={region}>{region}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <Select value={selectedCountry} onValueChange={handleCountryChange}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black border border-[#EC4899]/30">
                <SelectItem value="all">All</SelectItem>
                {countries.map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <Select value={selectedAccount} onValueChange={handleAccountNameChange}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black border border-[#10B981]/30">
                <SelectItem value="all">All</SelectItem>
                {accounts.map(account => <SelectItem key={account} value={account}>{account}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <RadioGroup defaultValue="product" className="flex flex-col items-start space-y-0 text-[10px]" value={filterType} onValueChange={value => handleFilterTypeChange(value as "product" | "industry")}>
              <div className="flex items-center space-x-1">
                <RadioGroupItem className="h-4 w-4" id="product" value="product" />
                <Label htmlFor="product" className="cursor-pointer text-white space-y-0 text-[10px]">Product</Label>
              </div>
              <div className="flex items-center space-x-1">
                <RadioGroupItem className="h-4 w-4" id="industry" value="industry" />
                <Label htmlFor="industry" className="cursor-pointer text-white space-y-0 text-[10px]">Industry</Label>
              </div>
            </RadioGroup>
            
            {filterType === "product" ? (
              <Select value={selectedProduct} onValueChange={handleProductLineChange}>
                <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black border border-[#F59E0B]/30">
                  <SelectItem value="all">All</SelectItem>
                  {products.map(product => <SelectItem key={product} value={product}>{product}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Select value={selectedIndustry} onValueChange={handleIndustryChange}>
                <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black border border-[#F59E0B]/30">
                  <SelectItem value="all">All</SelectItem>
                  {industries.map(industry => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterHeader;
